import Phaser from 'phaser';
import { puzzleNumber, isBeforeLaunch } from '@/lib/launch';
import type { Archetype } from '@/lib/archetypes';
import { gameBus } from '@/bridge/gameBus';
import { t } from '@/i18n';

export class MenuScene extends Phaser.Scene {
  private today!: string;
  private archetype!: Archetype;

  constructor() {
    super({ key: 'MenuScene' });
  }

  init(data: { archetype: Archetype }): void {
    this.archetype = data.archetype;
  }

  create(): void {
    this.today = this.registry.get('today') as string;

    gameBus.emit({
      type: 'scene:enter',
      payload: {
        scene: 'MENU',
        date: this.today,
        puzzleNumber: puzzleNumber(this.today),
        archetype: this.archetype,
      },
    });

    const prefs = this.registry.get('uiPrefs') as { getHardModeEnabled(): boolean } | undefined;
    const mode = prefs?.getHardModeEnabled() ? 'daily-hard' : 'daily';
    // Emit AFTER scene:enter — applySceneEnter→resetSceneScoped runs on
    // scene:enter and would clobber an earlier play:mode (Plan #1 §3.5).
    gameBus.emit({ type: 'play:mode', payload: { mode, hideHeatmap: mode === 'daily-hard' } });

    const { width, height } = this.scale;
    const cx = width / 2;

    // Brand title is rendered by DOM Header (and the persona card-hero).
    // Phaser puzzle-number text retained as a small fallback for slow DOM hydration.
    if (!isBeforeLaunch(this.today)) {
      this.add
        .text(cx, height * 0.3, `#${puzzleNumber(this.today)}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          color: '#cccccc',
        })
        .setOrigin(0.5);
    }

    // Play button: 280×80 rectangle centered horizontally, lower zone (0.85h).
    const btnY = height * 0.85;
    const btnW = 280;
    const btnH = 80; // ≥44 mobile target.
    const button = this.add
      .rectangle(cx, btnY, btnW, btnH, 0x2ecc71, 1)
      .setStrokeStyle(2, 0xffffff)
      .setInteractive({ useHandCursor: true });

    this.add
      .text(cx, btnY, t('menu.play'), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    button.on('pointerup', () => {
      // Read the pref FRESH at tap time — the player may have toggled Hard mode
      // in Settings (reachable from MENU via the header) after this scene rendered.
      const launchPrefs = this.registry.get('uiPrefs') as { getHardModeEnabled(): boolean } | undefined;
      const launchMode = launchPrefs?.getHardModeEnabled() ? 'daily-hard' : 'daily';
      this.scene.start('PlayScene', {
        date: this.today,
        archetype: this.archetype,
        existingShots: [],
        mode: launchMode,
      });
    });
  }
}
